'use client';

import React, { ReactElement } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  AnalyticsModuleTrackingProvider,
  AnalyticsModuleTrackingAttributes,
  useAnalyticsModuleTracking,
} from './AnalyticsModuleTracking';
import { useModuleViewedTracking } from './hooks/useModuleViewedTracking';
import { trackModuleViewed } from './eventHandlerTracking';

interface TrackableModuleProps {
  children: ReactElement;
  initialAttributes?: Partial<AnalyticsModuleTrackingAttributes>;
  /** Whether the tracking should fire only once when the element enters the viewport */
  triggerOnce?: boolean;
  /**
   * Margin around the root element when calculating intersections
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin
   */
  rootMargin?: string;
  /**
   * Threshold of visibility required to trigger the tracking
   * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/threshold
   */
  threshold?: number;
}

interface TrackableModuleWrapperProps {
  children: ReactElement;
  triggerOnce?: boolean;
  rootMargin?: string;
  threshold?: number;
}

/**
 * Determines the CTA type based on the content within a module element
 * @param element - The DOM element to analyze for CTA content
 * @returns The CTA type: 'enrollment', 'engagement', or 'No CTA'
 */
const getModuleCTAType = (element: HTMLElement): string => {
  // Check for enrollment-related elements
  const hasEnrollLink = !!element.querySelector('a[href*="/enroll"]');

  // Check for email capture elements
  const hasEmailCapture = !!element.querySelector('input[name="email"]');

  if (hasEnrollLink || hasEmailCapture) {
    return 'enrollment';
  }

  // Check for any links (engagement)
  const hasAnyLink = !!(
    element.querySelector('a[href]') ||
    element.querySelector('button') ||
    element.querySelector('[role="button"]')
  );

  return hasAnyLink ? 'engagement' : 'No CTA';
};

/**
 * Wraps a component to track when it becomes visible in the viewport
 * Uses IntersectionObserver to detect visibility and triggers analytics events
 *
 * @param props - Component props
 * @returns The provided child element with tracking capabilities
 */
const TrackableModuleWrapper = ({
  children,
  triggerOnce = true,
  rootMargin = '-100px 0',
  threshold = 0,
}: TrackableModuleWrapperProps) => {
  const trackModuleViewedSegment = useModuleViewedTracking();
  const { attributes } = useAnalyticsModuleTracking();
  const elementRef = React.useRef<HTMLElement>(null);

  // Set up the intersection observer for tracking when the module is in view
  const { ref: inViewRef } = useInView({
    triggerOnce,
    rootMargin,
    threshold,
    onChange: inView => {
      if (inView) {
        trackModuleViewedSegment();

        // Determine CTA type dynamically based on module content
        const ctaType = elementRef.current ? getModuleCTAType(elementRef.current) : 'No CTA';

        trackModuleViewed({
          module_title: attributes.moduleTitle,
          module_name: attributes.moduleName,
          module_index: attributes.moduleIndex,
          module_type: attributes.moduleType,
          cta_type: ctaType,
        });
      }
    },
  });

  // Combined ref callback that sets both refs
  const setRefs = React.useCallback(
    (node: HTMLElement | null) => {
      elementRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  return (
    <div ref={setRefs} data-module-index={attributes.moduleIndex}>
      {children}
    </div>
  );
};

/**
 * A component that tracks when a UI module becomes visible in the viewport
 * and sends analytics events with module metadata.
 *
 * Combines the AnalyticsModuleTrackingProvider for setting module attributes
 * with visibility tracking via IntersectionObserver.
 *
 * @example
 * ```tsx
 * <TrackableModule
 *   initialAttributes={{
 *     moduleTitle: 'Hero Banner',
 *     moduleType: 'hero',
 *     moduleIndex: '1',
 *     moduleLocation: 'https://www.example.com/hero'
 *   }}
 *   threshold={0.5}
 * >
 *   <HeroComponent />
 * </TrackableModule>
 * ```
 *
 * @param props - Component props
 * @returns The wrapped component with tracking capabilities
 */
const TrackableModule = ({
  children,
  initialAttributes,
  triggerOnce = true,
  rootMargin = '',
  threshold = 0.75,
}: TrackableModuleProps) => {
  return (
    <AnalyticsModuleTrackingProvider initialAttributes={initialAttributes}>
      <TrackableModuleWrapper triggerOnce={triggerOnce} rootMargin={rootMargin} threshold={threshold}>
        {children}
      </TrackableModuleWrapper>
    </AnalyticsModuleTrackingProvider>
  );
};

export { getModuleCTAType };
export default TrackableModule;
